/**
 * Words and arithmetic for "check email every…" (Settings → Email).
 * Pure, so the picker's wording is unit-tested rather than eyeballed.
 */

import { SYNC_INTERVAL_BOUNDS } from "@/lib/api/client";

/** The one-tap choices. Anything else is "Custom". */
export const SYNC_PRESETS: { minutes: number; label: string }[] = [
  { minutes: 5, label: "5 min" },
  { minutes: 15, label: "15 min" },
  { minutes: 30, label: "30 min" },
  { minutes: 60, label: "1 hour" },
  { minutes: 360, label: "6 hours" },
];

export type IntervalUnit = "minutes" | "hours";

/** "every 5 minutes", "every hour", "every 1 h 30 min", "every 6 hours". */
export function describeInterval(minutes: number): string {
  if (minutes === 60) return "every hour";
  if (minutes < 60) return `every ${minutes} minute${minutes === 1 ? "" : "s"}`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (m === 0) return `every ${h} hours`;
  return `every ${h} h ${m} min`;
}

/** How many automatic checks a day that means, rounded the way people say it. */
export function checksPerDay(minutes: number): string {
  const n = (24 * 60) / minutes;
  if (n <= 1) return "once a day";
  const rounded = Math.round(n);
  return `${Number.isInteger(n) ? "" : "about "}${rounded} checks a day`;
}

/**
 * A custom entry → minutes, or the sentence that says what is wrong with it.
 * Hours may be fractional ("1.5"); the result is whole minutes.
 */
export function parseCustom(
  raw: string,
  unit: IntervalUnit,
): { minutes: number } | { error: string } {
  const text = raw.trim();
  if (!text) return { error: "Enter how often." };
  const n = Number(text);
  if (!Number.isFinite(n) || n <= 0) return { error: "Enter a number above zero." };
  const minutes = Math.round(unit === "hours" ? n * 60 : n);
  if (unit === "minutes" && !Number.isInteger(n)) {
    return { error: "Use whole minutes." };
  }
  const { min, max } = SYNC_INTERVAL_BOUNDS;
  if (minutes < min || minutes > max) {
    return { error: `Choose between ${min} minutes and ${max / 60} hours.` };
  }
  return { minutes };
}

/** The friendliest unit to show a saved custom value in. */
export function splitForEditing(minutes: number): { value: string; unit: IntervalUnit } {
  if (minutes >= 60 && minutes % 60 === 0) return { value: String(minutes / 60), unit: "hours" };
  if (minutes >= 60 && minutes % 30 === 0) return { value: String(minutes / 60), unit: "hours" };
  return { value: String(minutes), unit: "minutes" };
}
